import { useEffect, useRef } from 'react';

/**
 * Picks up a new deploy without the shop pressing Ctrl+F5.
 *
 * The host sends index.html with no Cache-Control, so a browser that has
 * opened the shop before keeps running the old bundle until someone forces
 * a reload. Here the app asks the server for index.html itself (no-store)
 * and compares the main bundle it names with the one this page is running.
 * deploy.yml uploads index.html LAST, so once it names a new bundle every
 * file of that bundle is already on the server.
 *
 * A reload loses whatever is only in memory -- a cart on the sale screen, a
 * half-filled form -- so it only happens when nothing can be lost: right
 * after the app opened, or the moment the user moves to another page, and
 * never while a dialog is open or a field has focus. The session token lives
 * in sessionStorage, which a reload keeps, so nobody is signed out.
 */

const BUNDLE = /static\/js\/(main\.[0-9a-f]+\.js)/;
const CHECK_EVERY_MS = 5 * 60 * 1000;
const BOOT_WINDOW_MS = 15 * 1000;
const RELOAD_MARK = 'jinda_app_update_reload';
const RETRY_AFTER_MS = 10 * 60 * 1000;

/** The main bundle this page is running, or null (dev server, tests). */
export const runningBundle = (doc: Document = document): string | null => {
    for (const script of Array.from(doc.scripts)) {
        const m = BUNDLE.exec(script.getAttribute('src') || '');
        if (m) return m[1];
    }
    return null;
};

export const bundleNamedIn = (html: string): string | null => {
    const m = BUNDLE.exec(html || '');
    return m ? m[1] : null;
};

export const latestBundle = async (fetcher: typeof fetch = fetch): Promise<string | null> => {
    try {
        const res = await fetcher(`/index.html?app-update=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        return bundleNamedIn(await res.text());
    } catch {
        return null;
    }
};

/** Nothing on screen that a reload would throw away. */
export const safeToReload = (doc: Document = document): boolean => {
    const openDialog = Array.from(doc.querySelectorAll<HTMLElement>('.ant-modal-wrap, .ant-drawer-open'))
        .some(el => el.classList.contains('ant-drawer-open') || el.style.display !== 'none');
    if (openDialog) return false;
    const active = doc.activeElement as HTMLElement | null;
    if (active && (active.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName))) return false;
    return true;
};

/** One reload per new bundle; if the browser still served the old page, try again only later. */
const mayReloadFor = (target: string, now: number): boolean => {
    try {
        const mark = JSON.parse(window.sessionStorage.getItem(RELOAD_MARK) || 'null');
        if (mark && mark.target === target && now - Number(mark.at) < RETRY_AFTER_MS) return false;
        window.sessionStorage.setItem(RELOAD_MARK, JSON.stringify({ target, at: now }));
        return true;
    } catch {
        return false;
    }
};

export interface AppUpdateDeps {
    running?: () => string | null;
    latest?: () => Promise<string | null>;
    safe?: () => boolean;
    reload?: () => void;
    now?: () => number;
}

export const useAppUpdate = (pathname: string, deps: AppUpdateDeps = {}) => {
    const {
        running = runningBundle,
        latest = latestBundle,
        safe = safeToReload,
        reload = () => window.location.reload(),
        now = Date.now,
    } = deps;
    const pending = useRef<string | null>(null);
    const openedAt = useRef(now());
    const firstPath = useRef(pathname);
    const act = useRef({ safe, reload, now });
    act.current = { safe, reload, now };

    const tryReload = () => {
        const target = pending.current;
        if (!target || !act.current.safe()) return;
        if (mayReloadFor(target, act.current.now())) act.current.reload();
    };

    useEffect(() => {
        const mine = running();
        if (!mine) return undefined;
        let stopped = false;
        const check = async () => {
            const theirs = await latest();
            if (stopped || !theirs || theirs === mine) return;
            pending.current = theirs;
            if (act.current.now() - openedAt.current <= BOOT_WINDOW_MS) tryReload();
        };
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        check();
        const timer = window.setInterval(check, CHECK_EVERY_MS);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            stopped = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (pathname === firstPath.current) return;
        firstPath.current = pathname;
        tryReload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
};
